<template>
  <div
    class="ti-step"
    :class="{
      'ti-step--active': active,
      'ti-step--completed': completed,
      'ti-step--disabled': !active && !completed,
    }"
  >
    <div class="ti-step__header">
      <div class="ti-step__indicator">
        <wb-icon name="bds/checkmark/16" v-if="completed"></wb-icon>
        <span v-if="!completed">
          {{ index }}
        </span>
      </div>
      <div class="ti-step__expander">
        <div class="ti-step__title">
          <slot name="title">
            {{ $t(title) }}
          </slot>
        </div>

        <div class="ti-step__copy" v-if="$slots.copy">
          <slot name="copy">
            {{ copy }}
          </slot>
        </div>
      </div>
      <button
        v-if="expander !== false && (active || completed)"
        type="button"
        aria-label="Expand Button"
        class="ti-step__expander-icon-wrapper wb-round-button wb-round-button--small"
        @click="navigateToStep"
      >
        <wb-icon
          v-if="open && (active || !completed)"
          class="ti-step__expander-icon"
          name="bds/chevron-up/24"
        ></wb-icon>
        <wb-icon
          v-else
          class="ti-step__expander-icon"
          name="bds/chevron-down/24"
        ></wb-icon>
      </button>
    </div>

    <transition
      name="expand"
      @enter="expandAnimationEnter"
      @after-enter="expandAnimationAfterEnter"
      @leave="expandAnimationLeave"
    >
      <div class="ti-step__content" v-if="active && open">
        <slot></slot>
      </div>
    </transition>
  </div>
</template>
<script lang="ts" setup>
import { ref } from 'vue';
import { Step } from '@tradein/api';

interface Props {
  id: string;
  title: string;
  copy?: string;
  index: number;
  completed?: boolean;
  active?: boolean;
  expander?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(['navigateToStep']);
const open = ref(true);

const navigateToStep = (): void => {
  if (props.completed) {
    emit('navigateToStep', props.id);
  }
  if (props.active) {
    open.value = !open.value;
  }
};

const expandAnimationEnter = (element: HTMLElement | null): void => {
  if (element) {
    element.style.visibility = 'hidden';
    element.style.position = 'absolute';
    element.style.opacity = '0';
  }
};

const expandAnimationAfterEnter = (element: HTMLElement | null): void => {
  if (element) {
    const height = getComputedStyle(element).height;
    element.style.height = '0px';
    element.style.position = 'relative';
    element.style.visibility = 'visible';

    requestAnimationFrame(() => {
      element.style.height = height;
      element.style.opacity = '1';
    });

    setTimeout(() => (element.style.height = 'auto'), 500);
  }
};

const expandAnimationLeave = (element: HTMLElement | null): void => {
  if (element) {
    const height = getComputedStyle(element).height;
    element.style.height = height;

    requestAnimationFrame(() => {
      element.style.opacity = '0';
      element.style.height = '0px';
    });
  }
};
</script>
<style lang="scss">
.ti-step {
  position: relative;
  box-shadow:
    0 0 2px 0 rgba(0, 0, 0, 0.1),
    0 2px 4px 0 rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  border-left: 4px solid var(--wb-grey-70);
  margin-bottom: 12px;
  background-color: var(--wb-white);

  &:last-of-type {
    margin-bottom: 0;
  }

  @include breakpoint-from(mq4) {
    margin-left: 64px;
  }

  &--active {
    &::before {
      content: '';
      position: absolute;
      left: -4px;
      top: 0;
      width: 4px;
      height: 28px;
      background-color: var(--wb-blue-45);
      border-top-left-radius: 4px;

      @include breakpoint-from(mq4) {
        height: 38px;
      }
    }
  }

  &--completed {
    &::before {
      height: 100%;
    }

    border-left: 4px solid var(--wb-blue-45);
    .ti-step__indicator {
      background-color: var(--wb-white);
    }
  }

  &--disabled {
    .ti-step__indicator {
      background-color: var(--wb-grey-60);
      border: 0;
    }

    .ti-step__title {
      color: var(--wb-grey-60);
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    position: relative;

    @include breakpoint-from(mq4) {
      @include wb-spacing('padding', '24px s');
    }
  }

  &__expander-icon-wrapper {
    width: 36px;
    height: 36px;
    position: absolute;
    right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  &__indicator {
    @include wb-type-button-secondary;
    color: var(--wb-white);
    position: absolute;
    width: 24px;
    height: 24px;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 1px solid var(--wb-blue-45);
    background-color: var(--wb-blue-45);

    @include breakpoint-from(mq4) {
      width: 32px;
      height: 32px;
      left: -52px;
    }

    wb-icon {
      width: 16px;
      height: 16px;
      color: var(--wb-blue-45);
    }
  }

  &__expander {
    margin-left: 40px;

    @include breakpoint-from(mq4) {
      margin-left: 0;
    }
  }

  &__title {
    @include wb-type-copy-strong;
  }

  &__copy {
    @include wb-type-copy;
    padding-top: 4px;
    display: flex;
    align-items: center;
    color: var(--wb-grey-45);

    .ti-modal {
      margin-left: 4px;
    }
  }

  &__content {
    @include wb-spacing('padding', '0 s');
    transition:
      opacity 0.2s $wb-fade 0.5s,
      height 0.5s $wb-fade;
  }
}

.expand-leave-active {
  height: auto;
  opacity: 1;
  transition:
    opacity 0.2s $wb-fade,
    height 0.5s $wb-fade 0.2s;
}

.expand-leave-to {
  opacity: 0;
  height: 0;
  overflow: hidden;
}
</style>
